

import React, { useState } from "react";
import {
  Box, Grid, Card, Typography, TextField, Button, IconButton,
  InputAdornment, Checkbox, CircularProgress, FormControlLabel,
  Stepper, Step, StepLabel, MenuItem, useMediaQuery, useTheme
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CustomDialog from "../../../CustomDialog";
import addressConfig from "../../../Configurations/addressConfig.json";
import { useNavigate } from "react-router-dom";
import ayujalogo from "../../Logos/AuthScreens/AuthLogo.png";
import { apiRequest } from '../../../fetchSetup';

const COMMUNITY_OPTIONS = addressConfig.communities;
const BLOCK_OPTIONS = addressConfig.blocks;
const COMMUNITY_DETAILS = addressConfig.communityDetails;
const API_BASE = process.env.REACT_APP_API_URL;

export const fetchUserFromRedis = async (userId, token) => {
  if (!userId || !token) return null;
  try {
    const response = await apiRequest(`${API_BASE}/user/${userId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const data = await response.json();
    return data.user;
  } catch (error) {
    console.error("Error fetching user from Redis:", error);
    return null;
  }
};

const Register = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));       // < 600px
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md")); // 600–900px
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));  // < 900px

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [errors, setErrors] = useState({});
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: "", email: "", phone: "", password: "", confirmPassword: "",
    role: "resident",
    address: {
      full_name: "", phone: "", community_name: "", address_line1: "",
      address_line2: "", landmark: "", city: "", state: "", pincode: "",
      address_type: "home"
    },
    emergency_contacts: [{ name: "", phone: "" }],
    family_members: [{ name: "", relation: "", age: "", gender: "" }],
  });

  const [dialog, setDialog] = useState({ open: false, message: "", title: "", type: "info" });

  const steps = ['Basic Info', 'Address', 'Emergency', 'Family', 'Review'];

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleAddressChange = (field, value) =>
    setFormData({ ...formData, address: { ...formData.address, [field]: value } });

  const handleEmergencyChange = (index, field, value) => {
    const updated = [...formData.emergency_contacts];
    updated[index][field] = value;
    setFormData({ ...formData, emergency_contacts: updated });
  };

  const addEmergencyContact = () =>
    setFormData({ ...formData, emergency_contacts: [...formData.emergency_contacts, { name: "", phone: "" }] });

  const removeEmergencyContact = (index) =>
    setFormData({ ...formData, emergency_contacts: formData.emergency_contacts.filter((_, i) => i !== index) });

  const handleFamilyChange = (index, field, value) => {
    const updated = [...formData.family_members];
    updated[index][field] = value;
    setFormData({ ...formData, family_members: updated });
  };

  const addFamilyMember = () =>
    setFormData({ ...formData, family_members: [...formData.family_members, { name: "", relation: "", age: "", gender: "" }] });

  const removeFamilyMember = (index) =>
    setFormData({ ...formData, family_members: formData.family_members.filter((_, i) => i !== index) });

  const validateStep = (step) => {
    switch (step) {
      case 0:
        if (!formData.name || !formData.email || !formData.phone || !formData.password || !formData.confirmPassword) {
          setDialog({ open: true, type: "error", title: "Validation Error", message: "Please fill all required fields" });
          return false;
        }
        if (formData.name.trim().length < 3) {
          setDialog({ open: true, type: "error", title: "Invalid Name", message: "Name must be at least 3 characters long" });
          return false;
        }
        const passwordRegex = /^(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
        if (!passwordRegex.test(formData.password)) {
          setDialog({ open: true, type: "error", title: "Weak Password", message: "Password must contain at least one uppercase letter, one number, one special character, and be at least 8 characters long." });
          return false;
        }
        if (formData.password !== formData.confirmPassword) {
          setDialog({ open: true, type: "error", title: "Password Mismatch", message: "Passwords do not match" });
          return false;
        }
        if (!formData.termsAccepted) {
          setDialog({ open: true, type: "error", title: "Validation Error", message: "Please accept the terms and conditions" });
          return false;
        }
        return true;

      case 1:
        const requiredFields = ['community_name', 'address_line1', 'address_line2', 'city', 'state', 'pincode'];
        if (requiredFields.some(f => !formData.address[f])) {
          setDialog({ open: true, type: "error", title: "Required Fields Missing", message: "Please fill in all required address fields" });
          return false;
        }
        if (!/^\d{6}$/.test(formData.address.pincode)) {
          setDialog({ open: true, type: "error", title: "Invalid Pincode", message: "Pincode must be 6 digits" });
          return false;
        }
        if (!/^\d{10}$/.test(formData.address.phone)) {
          setDialog({ open: true, type: "error", title: "Invalid Phone", message: "Phone must be 10 digits" });
          return false;
        }
        return true;

      case 2:
        const hasEmergencyData = formData.emergency_contacts.some(c => c.name || c.phone);
        if (hasEmergencyData) {
          const incomplete = formData.emergency_contacts.some(c => (c.name && !c.phone) || (!c.name && c.phone));
          if (incomplete) {
            setDialog({ open: true, type: "warning", title: "Incomplete Data", message: "Please complete all emergency contact fields or leave them empty to skip" });
            return false;
          }
        }
        return true;

      case 3:
        const hasFamilyData = formData.family_members.some(m => m.name || m.relation || m.age || m.gender);
        if (hasFamilyData) {
          const incomplete = formData.family_members.some(m => {
            const filled = [m.name, m.relation, m.age, m.gender].filter(Boolean).length;
            return filled > 0 && filled < 4;
          });
          if (incomplete) {
            setDialog({ open: true, type: "warning", title: "Incomplete Data", message: "Please complete all family member fields or leave them empty to skip" });
            return false;
          }
        }
        return true;

      default:
        return true;
    }
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      if (activeStep === 0) {
        setFormData(prev => ({
          ...prev,
          address: { ...prev.address, full_name: prev.name, phone: prev.phone }
        }));
      }
      setActiveStep(s => s + 1);
    }
  };

  const handleBack = () => setActiveStep(s => s - 1);
  const handleSkip = () => setActiveStep(s => s + 1);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const filteredEmergencyContacts = formData.emergency_contacts
        .filter(c => c.name && c.phone)
        .map(c => `${c.name}:${c.phone}`);

      const filteredFamilyMembers = formData.family_members
        .filter(m => m.name && m.relation && m.age && m.gender)
        .map(m => `${m.name}:${m.relation}:${m.age}:${m.gender}`);

      const payload = {
        name: formData.name, email: formData.email, phone: formData.phone,
        password: formData.password, role: formData.role,
        address: { ...formData.address },
        emergency_contacts: filteredEmergencyContacts,
        family_members: filteredFamilyMembers,
      };

      const signupAPI = process.env.REACT_APP_REGISTER_URL;
      const response = await apiRequest(signupAPI, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await response.json();

      if (data.success) {
        setRegistrationSuccess(true);
        if (data.user.approval_status !== "pending") {
          if (data.token) sessionStorage.setItem("token", data.token);
          sessionStorage.setItem("userId", data.user.userId);
          setTimeout(() => {
            const userRole = data.user.role?.toLowerCase().trim();
            if (userRole === "resident") {
              sessionStorage.clear();
              setDialog({
                open: true, type: "success", title: "Registration Completed",
                message: "Registration completed. Please wait while our team verifies your details.",
                onPrimaryAction: () => navigate("/", { replace: true })
              });
            } else {
              window.location.href = "/admin-dashboard";
            }
          }, 2000);
        }
      } else {
        setDialog({ open: true, type: "error", title: "Registration Failed", message: data.error || "Something went wrong" });
      }
    } catch (error) {
      setDialog({ open: true, type: "error", title: "Registration Failed", message: error.message || "Something went wrong. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  const applyCommunityDetails = (community) => {
    if (COMMUNITY_DETAILS?.[community]) {
      const details = COMMUNITY_DETAILS[community];
      setFormData(prev => ({
        ...prev,
        address: {
          ...prev.address,
          address_line1: "", address_line2: "", landmark: "",
          community_name: community,
          city: details.city || "", state: details.state || "", pincode: details.pincode || ""
        }
      }));
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <>
            <TextField
              label="Full Name" name="name" value={formData.name}
              onChange={(e) => { if (/^[A-Za-z ]*$/.test(e.target.value)) handleChange(e); }}
              placeholder="Enter your full name" fullWidth margin="normal" required
              size={isMobile ? "small" : "medium"}
            />
            <TextField
              label="Email Address" name="email" type="email" value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value.toLowerCase() })}
              placeholder="Enter your email address" fullWidth margin="normal" required
              size={isMobile ? "small" : "medium"}
            />
            <TextField
              label="Mobile Number" name="phone" value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, "").slice(0, 10) })}
              placeholder="Enter 10-digit mobile number" fullWidth margin="normal" required
              size={isMobile ? "small" : "medium"}
              InputProps={{ startAdornment: <InputAdornment position="start">+91</InputAdornment> }}
            />
            <TextField
              label="Password" name="password" type={showPassword ? "text" : "password"}
              value={formData.password} onChange={handleChange}
              placeholder="Enter your password" fullWidth required margin="normal"
              size={isMobile ? "small" : "medium"}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} size="small">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Confirm Password" name="confirmPassword"
              type={showConfirmPassword ? "text" : "password"}
              value={formData.confirmPassword} onChange={handleChange}
              placeholder="Re-enter your password" fullWidth required margin="normal"
              size={isMobile ? "small" : "medium"}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} size="small">
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={formData.termsAccepted || false}
                  onChange={(e) => setFormData({ ...formData, termsAccepted: e.target.checked })}
                />
              }
              label={
                <Typography variant="body2">
                  Accept Ayuja's{" "}
                  <a href="/terms&conditions" target="_blank" rel="noopener noreferrer"
                    style={{ color: "#007a85", fontWeight: "bold", textDecoration: "none" }}>
                    Terms and Conditions
                  </a>
                </Typography>
              }
            />
          </>
        );

      case 1:
        return (
          <>
            <Typography variant="h6" sx={{ mb: 1, color: "#007a85", fontSize: { xs: "15px", md: "18px" } }}>
              Address (Required)
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
              Add your address to deliver medicine.
            </Typography>

            <Grid container spacing={2}>
              {/* Hidden fields */}
              <Grid item xs={12} sx={{ display: "none" }}>
                <TextField label="Full Name" value={formData.address.full_name}
                  onChange={(e) => handleAddressChange("full_name", e.target.value)} fullWidth size="small" />
              </Grid>
              <Grid item xs={12} sx={{ display: "none" }}>
                <TextField label="Phone Number" value={formData.address.phone}
                  onChange={(e) => handleAddressChange("phone", e.target.value.replace(/\D/g, "").slice(0, 10))}
                  fullWidth size="small"
                  InputProps={{ startAdornment: <InputAdornment position="start">+91</InputAdornment> }} />
              </Grid>

              <Grid item xs={12}>
                <TextField select required label="Community/Society Name" style={{width:210}}
                  value={formData.address.community_name} fullWidth size="small">
                  <MenuItem value="">Select Community</MenuItem>
                  {COMMUNITY_OPTIONS.map((community) => (
                    <MenuItem key={community} value={community}
                      onClick={() => applyCommunityDetails(community)}>
                      {community}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField select required label="Block" style={{width:210}}
                  value={formData.address.address_line1}
                  onChange={(e) => handleAddressChange("address_line1", e.target.value)}
                  fullWidth size="small" error={!!errors?.block} helperText={errors?.block}>
                  <MenuItem value="">Select Block</MenuItem>
                  {BLOCK_OPTIONS.map((block) => (
                    <MenuItem key={block} value={block}>{block}</MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
              <TextField
            label="Flat Number"
            required
            type="number"
            value={formData.address.address_line2}
            onChange={(e) => handleAddressChange("address_line2", e.target.value)}
            placeholder="Enter Flat Number"
            fullWidth
            size="small"
            inputProps={{ min: 0 }}
          />
            </Grid>

              <Grid item xs={12}>
                <TextField label="Landmark (Optional)" value={formData.address.landmark}
                  onChange={(e) => handleAddressChange("landmark", e.target.value)}
                  placeholder="Nearby landmark" fullWidth size="small" />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField label="City" required value={formData.address.city}
                  onChange={(e) => handleAddressChange("city", e.target.value)}
                  placeholder="Enter city" fullWidth size="small"
                  InputProps={{ sx: { backgroundColor: (!!formData.address.community_name && !!COMMUNITY_DETAILS?.[formData.address.community_name]) ? '#f5f5f5' : 'white' } }} />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField label="State" required value={formData.address.state}
                  onChange={(e) => handleAddressChange("state", e.target.value)}
                  placeholder="Enter state" fullWidth size="small"
                  InputProps={{ sx: { backgroundColor: (!!formData.address.community_name && !!COMMUNITY_DETAILS?.[formData.address.community_name]) ? '#f5f5f5' : 'white' } }} />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField label="Pincode" required value={formData.address.pincode}
                  onChange={(e) => handleAddressChange("pincode", e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="6-digit pincode" fullWidth size="small"
                  InputProps={{ sx: { backgroundColor: (!!formData.address.community_name && !!COMMUNITY_DETAILS?.[formData.address.community_name]) ? '#f5f5f5' : 'white' } }} />
              </Grid>
            </Grid>
          </>
        );

      case 2:
        return (
          <>
            <Typography variant="h6" sx={{ mb: 1, color: "#007a85", fontSize: { xs: "15px", md: "18px" } }}>
              Emergency Contacts (Optional)
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
              Add emergency contacts who we can reach in case of urgency.
            </Typography>

            {formData.emergency_contacts.map((contact, idx) => (
              <Card key={idx} sx={{ mb: 2, p: { xs: 1.5, md: 2 }, backgroundColor: "#f9f9f9" }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={5}>
                    <TextField
                      label="Contact Name" required value={contact.name}
                      onChange={(e) => handleEmergencyChange(idx, "name", e.target.value)}
                      fullWidth size="small"
                      error={contact.name.length > 0 && contact.name.length < 3}
                      helperText={contact.name.length > 0 && contact.name.length < 3 ? "Min 3 characters" : ""}
                    />
                  </Grid>
                  <Grid item xs={10} sm={5}>
                    <TextField
                      label="Phone Number" required value={contact.phone}
                      onChange={(e) => handleEmergencyChange(idx, "phone", e.target.value.replace(/\D/g, "").slice(0, 10))}
                      fullWidth size="small"
                      InputProps={{ startAdornment: <InputAdornment position="start">+91</InputAdornment> }}
                    />
                  </Grid>
                  <Grid item xs={2} sm={2} sx={{ display: "flex", justifyContent: "center" }}>
                    {formData.emergency_contacts.length > 1 && (
                      <IconButton color="error" onClick={() => removeEmergencyContact(idx)} size="small">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Grid>
                </Grid>
              </Card>
            ))}

            <Button variant="outlined" onClick={addEmergencyContact}
              sx={{ color: "#007a85", borderColor: "#007a85", textTransform: "none", "&:hover": { borderColor: "#005f64" }, fontSize: { xs: "13px", md: "14px" } }}>
              + Add Another Contact
            </Button>
          </>
        );

      case 3:
        return (
          <>
            <Typography variant="h6" sx={{ mb: 1, color: "#007a85", fontSize: { xs: "15px", md: "18px" } }}>
              Family Members (Optional)
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
              Add family member details for better personalized care.
            </Typography>

            {formData.family_members.map((member, idx) => (
              <Card key={idx} sx={{ mb: 2, p: { xs: 1.5, md: 2 }, backgroundColor: "#f9f9f9" }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Name" required value={member.name}
                      onChange={(e) => handleFamilyChange(idx, "name", e.target.value)}
                      fullWidth size="small"
                      error={member.name.length > 0 && member.name.length < 3}
                      helperText={member.name.length > 0 && member.name.length < 3 ? "Min 3 characters" : ""}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Relation" required value={member.relation}
                      onChange={(e) => handleFamilyChange(idx, "relation", e.target.value)}
                      fullWidth size="small" placeholder="e.g., Mother, Father"
                    />
                  </Grid>
                  <Grid item xs={5} sm={4}>
                    <TextField
                      label="Age" type="number" required value={member.age}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === "" || (Number(value) >= 1 && Number(value) <= 120))
                          handleFamilyChange(idx, "age", value);
                      }}
                      inputProps={{ min: 1, max: 120 }} fullWidth size="small"
                    />
                  </Grid>
                  <Grid item xs={5} sm={4}>
                    <TextField
                      select label="Gender" required value={member.gender}
                      onChange={(e) => handleFamilyChange(idx, "gender", e.target.value)}
                      sx={{ minWidth: 200 }} 
                      fullWidth size="small">
                      <MenuItem value="">Select</MenuItem>
                      <MenuItem value="male">Male</MenuItem>
                      <MenuItem value="female">Female</MenuItem>
                      <MenuItem value="other">Prefer not to say</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={2} sm={4} sx={{ display: "flex", justifyContent: "center" }}>
                    {formData.family_members.length > 1 && (
                      <IconButton color="error" onClick={() => removeFamilyMember(idx)} size="small">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Grid>
                </Grid>
              </Card>
            ))}

            <Button variant="outlined" onClick={addFamilyMember}
              sx={{ color: "#007a85", borderColor: "#007a85", textTransform: "none", "&:hover": { borderColor: "#005f64" }, fontSize: { xs: "13px", md: "14px" } }}>
              + Add Family Member
            </Button>
          </>
        );

      case 4:
        return (
          <>
            <Typography variant="h6" sx={{ mb: 2, color: "#007a85", fontSize: { xs: "15px", md: "18px" } }}>
              Review Your Information
            </Typography>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, color: "#007a85" }}>
                Personal Information
              </Typography>
              <Typography variant="body2"><strong>Name:</strong> {formData.name}</Typography>
              <Typography variant="body2"><strong>Email:</strong> {formData.email}</Typography>
              <Typography variant="body2"><strong>Phone:</strong> +91{formData.phone}</Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, color: "#007a85" }}>
                Delivery Address
              </Typography>
              <Typography variant="body2"><strong>Community:</strong> {formData.address.community_name}</Typography>
              <Typography variant="body2"><strong>Block:</strong> {formData.address.address_line1}</Typography>
              <Typography variant="body2"><strong>Flat:</strong> {formData.address.address_line2}</Typography>
              <Typography variant="body2">{formData.address.city}, {formData.address.state} - {formData.address.pincode}</Typography>
              {formData.address.landmark && (
                <Typography variant="body2"><strong>Landmark:</strong> {formData.address.landmark}</Typography>
              )}
            </Box>

            {formData.emergency_contacts.some(c => c.name && c.phone) && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, color: "#007a85" }}>
                  Emergency Contacts
                </Typography>
                {formData.emergency_contacts.filter(c => c.name && c.phone).map((contact, idx) => (
                  <Typography key={idx} variant="body2">{contact.name} - +91{contact.phone}</Typography>
                ))}
              </Box>
            )}

            {formData.family_members.some(m => m.name && m.relation) && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, color: "#007a85" }}>
                  Family Members
                </Typography>
                {formData.family_members.filter(m => m.name && m.relation).map((member, idx) => (
                  <Typography key={idx} variant="body2">
                    {member.name} - {member.relation}, {member.age} yrs, {member.gender}
                  </Typography>
                ))}
              </Box>
            )}
          </>
        );

      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        p: { xs: 2, sm: 3, md: 4 },
        backgroundColor: "#f0f4f8",
      }}
    >
      <Card
        sx={{
          borderRadius: "16px",
          width: "100%",
          maxWidth: { xs: "100%", sm: "540px", md: "960px" },
          overflow: "hidden",
          boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
        }}
      >
        <Grid container sx={{ flexWrap: { xs: "wrap", md: "nowrap" } }}>

          {/* ── LEFT SECTION: Branding ── */}
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              background: "linear-gradient(135deg, #004f54, #007a85)",
              color: "white",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              p: { xs: 3, md: 4 },
              flex: { md: "0 0 50%" },
              maxWidth: { md: "50%" },
            }}
          >
            {/* Logo */}
            <Box sx={{ display: "flex", justifyContent: "center", mb: { xs: 1, md: 2 } }}>
              <img src={ayujalogo} alt="Ayuja Logo"
                style={{ height: isMobile ? "52px" : isTablet ? "62px" : "80px", width: "auto" }} />
            </Box>

            {/* Title */}
            <Typography variant={isMobile ? "h6" : "h4"}
              sx={{ fontWeight: "bold", mb: { xs: 0.5, md: 2 }, textAlign: "center" }}>
              {isMobile ? "Join Ayuja Today" : "Join \nAyuja Today"}
            </Typography>

            {/* Tagline - hidden on mobile */}
            {!isMobile && (
              <Typography variant="h6" sx={{ mb: 2, textAlign: "center", fontSize: { sm: "14px", md: "18px" } }}>
                Transform lives through personalized in-home care. Become part of a community that values
                compassion, dignity, and exceptional service.
              </Typography>
            )}

            {/* Feature pills */}
            <Box sx={{ display: "flex", justifyContent: "center", gap: { xs: 2, md: 3 }, mb: { xs: 1.5, md: 4 }, flexWrap: "wrap" }}>
              <Typography variant="body2">✅ 24/7 Support</Typography>
              <Typography variant="body2">✅ Certified Caregivers</Typography>
            </Box>

            {/* Emergency box - hidden on mobile, compact on tablet */}
            {!isMobile && (
              <Box sx={{
                backgroundColor: "rgba(255,255,255,0.1)", borderRadius: "12px",
                p: { sm: 2, md: 3 }, textAlign: "center", width: { sm: "85%", md: "80%" }
              }}>
                <Typography variant="subtitle1" fontWeight="bold" mb={1}
                  sx={{ fontSize: { sm: "13px", md: "16px" } }}>
                  Emergency Support Available
                </Typography>
                {!isTablet && (
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    Need immediate assistance? Our emergency support team is available 24/7.
                  </Typography>
                )}
                <Button variant="contained" onClick={() => navigate("/contactus")}
                  sx={{
                    backgroundColor: "#80ED99", "&:hover": { backgroundColor: "#6dda86" },
                    color: "#22577A", textTransform: "none", borderRadius: "8px",
                    fontSize: { sm: "12px", md: "14px" }
                  }}>
                  Contact Emergency
                </Button>
              </Box>
            )}

            {/* Mobile emergency CTA */}
            {isMobile && (
              <Button variant="outlined" size="small" onClick={() => navigate("/contactus")}
                sx={{ borderColor: "#80ED99", color: "#80ED99", textTransform: "none", borderRadius: "8px", fontSize: "12px" }}>
                🚨 Contact Emergency
              </Button>
            )}
          </Grid>

          {/* ── RIGHT SECTION: Form ── */}
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              backgroundColor: "white",
              p: { xs: 3, sm: 3, md: 4 },
              display: "flex",
              flexDirection: "column",
              flex: { md: "0 0 50%" },
              maxWidth: { md: "50%" },
            }}
          >
            <Typography variant={isMobile ? "h6" : "h5"} sx={{ fontWeight: "bold", mb: 0.5 }}>
              Create Account
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
              Get started with your personalized care dashboard
            </Typography>

            {/* Stepper */}
            <Stepper
              activeStep={activeStep}
              sx={{
                mb: { xs: 2, md: 3 },
                // On mobile use alternativeLabel (labels below icons) to save horizontal space
                ...(isMobile && { overflowX: "auto" }),
              }}
              alternativeLabel={isMobile}
            >
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel
                    sx={{
                      '& .MuiStepLabel-label': { fontSize: { xs: '0.6rem', md: '0.7rem' } },
                      '& .MuiStepIcon-root': { width: { xs: 20, md: 24 }, height: { xs: 20, md: 24 } },
                      '& .MuiStepIcon-root.Mui-active, & .MuiStepIcon-root.Mui-completed': { color: '#007a85' },
                    }}
                  >
                    {label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>

            {/* Step Content */}
            <Box
              sx={{
                mb: 2,
                flexGrow: 1,
                minHeight: { xs: "280px", sm: "320px", md: "380px" },
                maxHeight: { xs: "340px", sm: "380px", md: "430px" },
                overflowY: "auto",
                pr: 0.5,
                // Custom scrollbar
                "&::-webkit-scrollbar": { width: "4px" },
                "&::-webkit-scrollbar-track": { background: "#f1f1f1", borderRadius: "4px" },
                "&::-webkit-scrollbar-thumb": { background: "#007a85", borderRadius: "4px" },
              }}
            >
              {renderStepContent(activeStep)}
            </Box>

            {/* Navigation Buttons */}
            <Box sx={{ display: "flex", gap: 1.5, mt: "auto" }}>
              {activeStep > 0 && (
                <Button
                  variant="outlined" onClick={handleBack}
                  startIcon={<ArrowBackIcon fontSize="small" />}
                  sx={{
                    flex: 1, borderColor: "#007a85", color: "#007a85",
                    textTransform: "none", fontSize: { xs: "12px", md: "14px" }, py: { xs: 0.8, md: 1 }
                  }}>
                  Back
                </Button>
              )}

              {activeStep > 1 && activeStep < 4 && (
                <Button variant="text" onClick={handleSkip}
                  sx={{ color: "#666", fontSize: { xs: "12px", md: "14px" }, px: { xs: 1, md: 2 } }}>
                  Skip
                </Button>
              )}

              {activeStep < 4 ? (
                <Button
                  variant="contained" onClick={handleNext}
                  endIcon={<ArrowForwardIcon fontSize="small" />}
                  sx={{
                    flex: 1, backgroundColor: "#007a85", textTransform: "none",
                    "&:hover": { backgroundColor: "#005f64" },
                    fontSize: { xs: "12px", md: "14px" }, py: { xs: 0.8, md: 1 }
                  }}>
                  Continue
                </Button>
              ) : (
                <Button
                  variant="contained" onClick={handleSubmit} disabled={loading}
                  sx={{
                    flex: 1, backgroundColor: "#007a85", textTransform: "none",
                    "&:hover": { backgroundColor: "#005f64" },
                    fontSize: { xs: "12px", md: "14px" }, py: { xs: 0.8, md: 1 }
                  }}>
                  {loading ? <CircularProgress size={20} color="inherit" /> : "Register"}
                </Button>
              )}
            </Box>

            <Typography sx={{ textAlign: "center", mt: 2, fontSize: { xs: "13px", md: "14px" } }}>
              Already have an account?{" "}
              <a href="/Login" style={{ color: "#007a85", fontWeight: "bold", textDecoration: "none" }}>
                Sign In
              </a>
            </Typography>
          </Grid>
        </Grid>
      </Card>

      <CustomDialog
        open={dialog.open}
        title={dialog.title}
        message={dialog.message}
        type={dialog.type}
        onClose={() => setDialog({ ...dialog, open: false })}
        onPrimaryAction={dialog.onPrimaryAction}
      />
    </Box>
  );
};

export default Register;